/*
 * File: Meta.jsx
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:22 PM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: July 21, 2022 at 10:58 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

// Utilities
import { getCDNURL } from '../../../utilities/utilities';


/**
 * Helpers
 */

export const buildDynamicMeta = (content, data) => {

	// If no dynamic data, return template
	if (!data || !content) return content;

	// Initialize dynamic content
	let dynamicContent = content;

	// Search for template matches
	const matches = dynamicContent.match(/\{{(.*?)\}}/g);

	// Replace content
	matches?.forEach((match) => {
		const refObj = match.replace(/[{}]/g, '');
		const refs = refObj.split('.');
		let value = data;
		refs?.forEach((ref, index) => {
			if (value != null) {
				value = value[ref];
			} else {
				value = index === refs.length - 1 ? '' : {};
			}
		});
		if (value) value = value.replace(/\s\s+/g, ' ').trim();
		dynamicContent = dynamicContent.replace(match, value);
	});

	// Return content
	return dynamicContent;
};


/**
 * Component
 */

export const Meta = ({
	meta, locale, merchant, data
}) => {

	// Build full data
	const dataObj = {
		...data,
		merchant
	};

	// Create merchant domain
	let merchantDomain = `https://${merchant.domain}`;
	if (merchant.redirectToSubdomain) {
		merchantDomain = `https://${merchant.redirectToSubdomain}.${merchant.domain}`;
	}

	// Get CDN URL
	const CDNURL = getCDNURL(merchant);

	// Build meta title elements
	const title = buildDynamicMeta(meta.title, dataObj);
	const ogTitle = buildDynamicMeta(meta.ogTitle, dataObj);
	const twTitle = buildDynamicMeta(meta.twTitle, dataObj);

	// Build meta description elements
	const description = buildDynamicMeta(meta.description, dataObj);
	const ogDescription = buildDynamicMeta(meta.ogDescription, dataObj);
	const twDescription = buildDynamicMeta(meta.twDescription, dataObj);

	// Build meta article elements
	const author = buildDynamicMeta(meta.author, data);
	const publishedTime = buildDynamicMeta(meta.publishedTime, data);
	const modifiedTime = buildDynamicMeta(meta.modifiedTime, data);

	// Create open graph meta parameters
	let ogImage = merchant.socialCover && merchant.socialCover.startsWith('http') ? merchant.socialCover : `${CDNURL}/public/social/${merchant.socialCover}`;
	if (meta.image) ogImage = meta.image;
	if (meta.ogImage) ogImage = meta.ogImage;

	// Create twitter meta parameters
	let twImage = merchant.socialCover && merchant.socialCover.startsWith('http') ? merchant.socialCover : `${CDNURL}/public/social/${merchant.socialCover}`;
	if (meta.image) twImage = meta.image;
	if (meta.twImage) twImage = meta.twImage;

	// Return component
	return (
		<>
			{/* Site Meta */}
			<Helmet prioritizeSeoTags>
				<title>{title}</title>
				{description && <meta name="description" content={description} />}
				<meta charset="UTF-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta name="theme-color" content="#000000" />
				{meta.shouldIndex === false && <meta name="robots" content="noindex" />}
			</Helmet>

			{/* Localization */}
			<Helmet prioritizeSeoTags>
				<link rel="canonical" href={locale.url ? locale.url.canonical : ''} />
				<link rel="alternate" hrefLang="en" href={locale.url ? `${merchantDomain}/en${locale?.url?.path}` : ''} />
				<link rel="alternate" hrefLang="x-default" href={locale.url ? `${merchantDomain}${locale?.url?.path}` : ''} />
			</Helmet>

			{/* Article Meta */}
			{meta.showArticle === true && (
				<Helmet prioritizeSeoTags>
					{(meta.fbSiteName || merchant.facebook?.url) && <meta property="article:publisher" content={meta.fbSiteName ? meta.fbSiteName : merchant.facebook?.url} />}
					{publishedTime && <meta property="article:published_time" content={publishedTime} />}
					{modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
				</Helmet>
			)}

			{/* Author Meta */}
			{author && (
				<Helmet prioritizeSeoTags>
					<meta name="author" content={author} />
				</Helmet>
			)}

			{/* Open Graph Meta */}
			{meta.showSocial !== false && (
				<Helmet prioritizeSeoTags>
					<meta property="og:title" content={ogTitle || title} />
					{(ogDescription || description) && <meta property="og:description" content={ogDescription || description} />}
					<meta property="og:type" content={meta.ogType ? meta.ogType : 'website'} />
					<meta property="og:url" content={meta.canonicalUrl} />
					<meta property="og:image" content={ogImage} />
					<meta property="og:site_name" content={meta.siteName ? meta.siteName : merchant.name} />
					<meta property="og:locale" content={locale.localeId} />
				</Helmet>
			)}

			{/* Twitter Meta */}
			{meta.showSocial !== false && (
				<Helmet prioritizeSeoTags>
					<meta name="twitter:card" content="summary" />
					{merchant.twitter && merchant.twitter.handle && <meta name="twitter:site" content={`@${merchant.twitter.handle}`} />}
					{merchant.twitter && merchant.twitter.handle && <meta name="twitter:creator" content={`@${merchant.twitter.handle}`} />}
					<meta name="twitter:title" content={twTitle || title} />
					{(twDescription || description) && <meta name="twitter:description" content={twDescription || description} />}
					<meta name="twitter:image" content={twImage} />
				</Helmet>
			)}

			{/* Mobile Meta */}
			<Helmet prioritizeSeoTags>
				<meta name="apple-mobile-web-app-title" content={meta.siteName ? meta.siteName : merchant.name} />
				<meta name="application-name" content={meta.siteName ? meta.siteName : merchant.name} />
			</Helmet>

			{/* Body */}
			<Helmet prioritizeSeoTags>
				<body className={meta.bodyClasses || 'hide-captcha'} />
			</Helmet>
		</>
	);
};


/**
 * Configuration
 */

Meta.displayName = 'Meta';
Meta.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	merchant: PropTypes.shape(),
	data: PropTypes.shape(),
};
Meta.defaultProps = {
	meta: {},
	locale: {},
	merchant: {},
	data: null
};
